<template>
  <div v-loading="loading">
    <el-select
        v-model="stateCode"
        :disabled="disabled"
        :allow-create="isTextInput || allowCreate"
        clearable
        no-data-text="Enter request state/province"
        filterable
        :placeholder="placeholder"
        name="state"
        style="width: 100%;">
      <el-option
          v-for="(state, _code) in states"
          v-if="isDomestic"
          :key="_code"
          :value="_code"
          :label="state.name">
        <span style="float: left;">{{ state.name }}</span>
        <span style="float: right; color: #999;">{{ _code | uppercase }}</span>
      </el-option>
      <el-option
          v-for="(state, _code) in states"
          v-if="!isDomestic"
          :key="state.name"
          :value="state.name"
          :label="state.name">
        <span style="float: left;">{{ state.name }}</span>
        <span style="float: right; color: #999;">{{ state.code | uppercase }}</span>
      </el-option>
    </el-select>
  </div>
</template>

<script>
import { isNil, isEmpty, trim, get, sortBy, keys } from "lodash";
import { createNamespacedHelpers } from "vuex";

let normalize = (v) => trim(isNil(v) ? "" : v);
const { mapGetters: sharedGetters, mapActions: sharedActions } =
  createNamespacedHelpers("shared");

export default {
  name: "CountryStateInput",
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    inputType: {
      type: String,
      required: true,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    // eslint-disable-next-line vue/require-prop-types
    country: {
      validator(val) {
        return val === null || typeof val === "string";
      },
      default: "",
    },
    value: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      placeholder: "State/Province",
      loading: false,
    };
  },
  computed: {
    ...sharedGetters(["countryStates"]),
    stateCode: {
      get() {
        let name, code;
        name = code = normalize(this.value);
        return this.isDropdown ? code.toUpperCase() : name;
      },
      set(code) {
        this.$emit("input", code);
      },
    },
    states() {
      let states = get(
        this.$store.getters["shared/countries"][this.country],
        "states",
        []
      );

      if (!this.isDomestic) {
        const statesByName = {};
        let stateNames = [];
        keys(states).forEach((stateCode) => {
          const stateName = states[stateCode]["name"];
          stateNames.push(stateName);
          statesByName[stateName] = states[stateCode];
        });
        stateNames = sortBy(stateNames, (stateName) => stateName);

        states = {};
        stateNames.forEach((stateName) => {
          states[stateName] = statesByName[stateName];
        });
      }

      return states;
    },
    isTextInput() {
      return this.inputType === "text";
    },
    isDropdown() {
      return this.inputType === "select";
    },
    isDomestic() {
      return ["US", "CA"].includes(this.country);
    },
  },
  watch: {
    country(code, oldCode) {
      [code, oldCode] = this.normalizeEmptyValues(code, oldCode);
      let countryChanged = code !== oldCode;
      if (countryChanged === false) return;
      if (this.isDropdown && !isEmpty(code)) this.loadStates();
      else this.stateCode = this.disabled ? this.stateCode : "";
    },
    disabled(newValue, oldValue) {
      if (!newValue) this.loadStates();
    },
  },
  methods: {
    ...sharedActions(["fetchCountryStates"]),
    loadStates() {
      this.loading = true;
      this.fetchCountryStates(String(this.country).toUpperCase())
        .then((_) => {
          if (typeof this.states[this.stateCode] === "undefined") {
            if (this.allowCreate) {
              this.stateCode = this.value;
            } else {
              this.stateCode = "";
            }
          }
        })
        .catch((_) => {
          this.$notify.warning({
            title: "States",
            message: "Couldn't be loaded. Reload the page.",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    normalizeEmptyValues(a, b) {
      let result = [];
      result.push(normalize(a));
      result.push(normalize(b));
      return result;
    },
  },
};
</script>
